/**
 * 按钮组件 JavaScript
 * 处理按钮的交互状态和动态行为
 */
class ButtonComponent {
  constructor() {
    this.init();
  }

  init() {
    this.setupButtons();
    this.setupEventListeners();
  }

  setupButtons() {
    // 获取所有按钮组件
    const buttons = document.querySelectorAll('.component-button');
    
    buttons.forEach(button => {
      this.setupButton(button);
    });
  }

  setupButton(button) {
    // 设置按钮的初始状态
    this.updateButtonState(button);
    
    // 添加事件监听器
    button.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    button.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    button.addEventListener('mouseleave', (e) => this.handleMouseLeave(e));
    button.addEventListener('touchstart', (e) => this.handleTouchStart(e));
    button.addEventListener('touchend', (e) => this.handleTouchEnd(e));
    button.addEventListener('keydown', (e) => this.handleKeyDown(e));
    button.addEventListener('keyup', (e) => this.handleKeyUp(e));
  }

  setupEventListeners() {
    // 监听动态添加的按钮
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const buttons = node.querySelectorAll ? node.querySelectorAll('.component-button') : [];
            if (node.classList && node.classList.contains('component-button')) {
              buttons.push(node);
            }
            buttons.forEach(button => this.setupButton(button));
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  updateButtonState(button) {
    const isDisabled = button.disabled || button.getAttribute('aria-disabled') === 'true';
    const hasPressedClass = button.classList.contains('component-button--pressed');
    
    if (isDisabled) {
      button.classList.remove('component-button--enabled', 'component-button--pressed');
      button.classList.add('component-button--disabled');
    } else if (hasPressedClass) {
      // 如果按钮有 pressed 类，保持 pressed 状态
      button.classList.remove('component-button--enabled', 'component-button--disabled');
      button.classList.add('component-button--pressed');
    } else {
      button.classList.remove('component-button--disabled', 'component-button--pressed');
      button.classList.add('component-button--enabled');
    }
  }

  handleMouseDown(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button)) {
      button.classList.remove('component-button--enabled');
      button.classList.add('component-button--pressed');
    }
  }

  handleMouseUp(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button)) {
      button.classList.remove('component-button--pressed');
      button.classList.add('component-button--enabled');
    }
  }

  handleMouseLeave(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button)) {
      button.classList.remove('component-button--pressed');
      button.classList.add('component-button--enabled');
    }
  }

  handleTouchStart(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button)) {
      button.classList.remove('component-button--enabled');
      button.classList.add('component-button--pressed');
    }
  }

  handleTouchEnd(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button)) {
      button.classList.remove('component-button--pressed');
      button.classList.add('component-button--enabled');
    }
  }

  handleKeyDown(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button) && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      button.classList.remove('component-button--enabled');
      button.classList.add('component-button--pressed');
    }
  }

  handleKeyUp(event) {
    const button = event.currentTarget;
    if (!this.isButtonDisabled(button) && (event.key === 'Enter' || event.key === ' ')) {
      button.classList.remove('component-button--pressed');
      button.classList.add('component-button--enabled');
    }
  }

  isButtonDisabled(button) {
    return button.disabled || button.getAttribute('aria-disabled') === 'true';
  }

  // 公共方法：设置按钮状态
  setButtonState(button, state) {
    const validStates = ['enabled', 'pressed', 'disabled'];
    if (!validStates.includes(state)) {
      console.warn(`Invalid button state: ${state}`);
      return;
    }

    // 移除所有状态类
    button.classList.remove('component-button--enabled', 'component-button--pressed', 'component-button--disabled');
    
    // 添加新状态类
    button.classList.add(`component-button--${state}`);
    
    // 更新 disabled 属性
    if (state === 'disabled') {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    }
  }

  // 公共方法：获取按钮状态
  getButtonState(button) {
    if (button.classList.contains('component-button--disabled')) {
      return 'disabled';
    } else if (button.classList.contains('component-button--pressed')) {
      return 'pressed';
    } else {
      return 'enabled';
    }
  }

  // 公共方法：设置按钮样式
  setButtonStyle(button, style) {
    const validStyles = ['primary', 'secondary', 'tertiary', 'text'];
    if (!validStyles.includes(style)) {
      console.warn(`Invalid button style: ${style}`);
      return;
    }

    // 移除所有样式类
    button.classList.remove('component-button--primary', 'component-button--secondary', 'component-button--tertiary', 'component-button--text');
    
    // 添加新样式类
    button.classList.add(`component-button--${style}`);
  }

  // 公共方法：设置按钮文本
  setButtonLabel(button, label) {
    const labelElement = button.querySelector('.component-button__label');
    if (labelElement) {
      labelElement.textContent = label;
    }
  }

  // 公共方法：设置按钮图标
  setButtonIcon(button, iconName) {
    const iconElement = button.querySelector('.component-button__icon');
    if (!iconElement) {
      console.warn('Button does not have an icon element');
      return;
    }

    // 移除所有图标类
    iconElement.innerHTML = '';
    
    // 根据图标名称添加相应的图标
    switch (iconName) {
      case 'arrow-left':
        iconElement.innerHTML = '<div class="component-button__icon-arrow-left"></div>';
        break;
      case 'arrow-right':
        iconElement.innerHTML = '<div class="component-button__icon-arrow-right"></div>';
        break;
      default:
        iconElement.innerHTML = '<div class="component-button__icon-default"></div>';
    }
    
    iconElement.setAttribute('data-content', iconName);
  }
}

// 初始化按钮组件
document.addEventListener('DOMContentLoaded', () => {
  window.buttonComponent = new ButtonComponent();
});

// 导出类以供外部使用
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ButtonComponent;
} 